<template>
  <!-- API Documentation -->
  <div class="api-doc">
    <!-- Instant Order Endpoint -->
    <section class="info-display">
      <h3>📤 Instant Order API Endpoint</h3>
      <p>
        Use this endpoint to
        <strong>place an instant service order</strong> (e.g., Find My iPhone,
        SIM lock, iCloud status).
      </p>
      <p>
        <strong>Endpoint:</strong><br />
        <a
          href="https://alpha.imeicheck.com/api/php-api/create?key=API_KEY&service=SERVICE_ID&imei=IMEI/SN"
          target="_blank"
        >
          https://alpha.imeicheck.com/api/php-api/create?key=API_KEY&service=SERVICE_ID&imei=IMEI/SN
        </a>
      </p>
      <h4>How to Use:</h4>
      <ul>
        <li>
          <code>API_KEY</code>: Replace with your API key (found in your account
          dashboard).
        </li>
        <li>
          <code>SERVICE_ID</code>: Replace with the numeric ID of the service
          you wish to use. This ID must be taken from the "PHP LIST" page, which
          displays a table of all available services and their corresponding IDs
          (e.g., <code>1</code> for Find My iPhone).
        </li>
        <li>
          <code>IMEI/SN</code>: Replace with the device's IMEI number or Serial
          Number.
        </li>
      </ul>
      <p>
        <strong>Example:</strong><br />
        <code
          >https://alpha.imeicheck.com/api/php-api/create?key=abc123xyz&service=1&imei=359111099999999</code
        >
      </p>
      <h4>Status Response Values:</h4>
      <ul>
        <li>
          <strong>success</strong> – The order was successfully processed.
        </li>
        <li><strong>failed</strong> – The order failed or was rejected.</li>
        <li>
          <strong>error</strong> – A system-level error occurred:
          <ul>
            <li>Invalid API Key</li>
            <li>Wrong IP address</li>
            <li>Insufficient Credit</li>
          </ul>
          <em
            >Note: These messages are internal and should not be shown to
            end-users.</em
          >
        </li>
      </ul>
    </section>

    <!-- Sample Find My iPhone JSON Explanation -->
    <section class="info-display">
      <h3>📦 Sample Response: Find My iPhone</h3>
      <p>
        This is an example of a successful API response when using the "Find My
        iPhone" service.
      </p>
      <pre><code>{
  "orderId": 74275656,
  "duration": "1.95s",
  "status": "success",
  "imei": "35698831919XXX",
  "price": "0.01",
  "result": "iPhone 15 Pro (A3104)<br>IMEI: 35698831919XXX<br>Find My iPhone: <font color=red><strong>ON</strong></font><br>",
  "object": {
    "model": "iPhone 15 Pro (A3104)",
    "imei": "35698831919XXX",
    "fmiOn": true,
    "fmiON": true
  }
}</code></pre>
      <h4>Explanation:</h4>
      <ul>
        <li><code>orderId</code>: Unique ID of the submitted order.</li>
        <li><code>duration</code>: Time taken to process the order.</li>
        <li>
          <code>status</code>: Shows the order outcome (e.g.,
          <code>success</code>).
        </li>
        <li><code>imei</code>: The IMEI submitted for this request.</li>
        <li><code>price</code>: The credit cost of this request.</li>
        <li>
          <code>result</code>: HTML content that can be displayed directly to
          the client (includes formatting).
        </li>
        <li>
          <code>object</code>: Structured data, returned only for services where
          the PHP LIST table marks <strong>OBJECT</strong> as "YES".
        </li>
      </ul>
    </section>

    <!-- Order History Endpoint -->
    <section class="info-display">
      <h3>📜 Order History API Endpoint</h3>
      <p>
        Use this endpoint to
        <strong>check the status or result of a specific order</strong> using
        its unique ID.
      </p>
      <p>
        <strong>Endpoint:</strong><br />
        <a
          href="https://alpha.imeicheck.com/api/php-api/history?key=API_KEY&orderId=ORDER_ID"
          target="_blank"
        >
          https://alpha.imeicheck.com/api/php-api/history?key=API_KEY&orderId=ORDER_ID
        </a>
      </p>
      <ul>
        <li><code>API_KEY</code>: Your account's API key.</li>
        <li>
          <code>ORDER_ID</code>: The ID of the order you want to retrieve.
        </li>
      </ul>
    </section>

    <section class="info-display">
      <h3>💰 Account Balance API Endpoint</h3>
      <p>
        Use this to <strong>check your current account balance</strong> (API
        credits).
      </p>
      <p>
        <strong>Endpoint:</strong><br />
        <a
          href="https://alpha.imeicheck.com/api/php-api/balance?key=API_KEY"
          target="_blank"
        >
          https://alpha.imeicheck.com/api/php-api/balance?key=API_KEY
        </a>
      </p>
      <ul>
        <li><code>API_KEY</code>: Your API key.</li>
        <li>
          <strong>Note:</strong> Credit is temporarily deducted when an order is
          submitted. If the order is rejected, that credit is refunded
          automatically.
        </li>
      </ul>
    </section>

    <!-- Notes -->
    <section class="info-display">
      <h3>📝 Important Notes & Best Practices</h3>
      <ul>
        <li>
          <strong>GET and POST methods:</strong> All API endpoints support both
          HTTP methods. Choose based on your system integration.
        </li>
        <li>
          <strong>Service Limits:</strong> Each service has a “LIMITS” field
          showing the <em>recommended</em> maximum number of simultaneous
          requests. In practice, many services can handle up to double this
          limit. However, consistently exceeding the limit may trigger firewall
          protections or increase the risk of request rejection.
        </li>
        <li>
          <strong>Use batching for bulk orders:</strong> When placing many
          orders, avoid sending them all at once. Group them into smaller
          batches to reduce the chance of rejection.
        </li>
        <li>
          <strong>Duplicate Order Protection:</strong> If you send the same IMEI
          or Serial Number again within 2 seconds, it will be rejected. This
          prevents accidental double charges or duplicate processing.
        </li>
      </ul>
    </section>
    <!-- Common Errors -->
    <section class="info-display">
      <h3>❗ Common Errors & Fixes</h3>
      <table class="error-table">
        <thead>
          <tr>
            <th>Error Message</th>
            <th>Description</th>
            <th>Suggested Fix</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Invalid ApiKey</td>
            <td>The API key used is incorrect or expired.</td>
            <td>Check and copy the correct API key from your dashboard.</td>
          </tr>
          <tr>
            <td>Wrong IP</td>
            <td>Your current IP address is not allowed.</td>
            <td>
              Go to the <strong>Linked IP</strong> section in your account. You
              can either click <strong>Reset IP</strong> to authorize your
              current IP, or click <strong>Disable</strong> to allow access from
              any IP.
            </td>
          </tr>
          <tr>
            <td>Credit Error</td>
            <td>Insufficient credits in your account.</td>
            <td>Top up your credit balance from the dashboard.</td>
          </tr>
        </tbody>
      </table>
    </section>
  </div>
</template>

<script type="module">
export default {};
</script>

<style lang="scss">
.api-doc {
  padding: 20px;
  font-family: Arial, sans-serif;
  line-height: 1.6;
}

.title {
  font-size: 1.8rem;
  margin-bottom: 24px;
}

.text-center {
  text-align: center;
}

.info-display {
  margin-bottom: 30px;
  padding: 20px;
  background: #f9f9fc;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.08);
  border-radius: 16px;
}

.info-display h3 {
  margin-bottom: 10px;
  font-size: 1.3rem;
}

.info-display h4 {
  margin-top: 15px;
  margin-bottom: 8px;
  font-weight: bold;
}

.info-display ul {
  margin-left: 20px;
  list-style-type: disc;
}

a {
  word-break: break-word;
  color: #007acc;
  text-decoration: none;
}

a:hover {
  text-decoration: underline;
}

code {
  background-color: #eee;
  padding: 2px 6px;
  border-radius: 4px;
  font-family: monospace;
}

.error-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 10px;
}

.error-table th,
.error-table td {
  border: 1px solid #ddd;
  padding: 8px;
  text-align: left;
}

.error-table th {
  background-color: #f0f0f0;
  font-weight: bold;
}
</style>
